import React, {useCallback, useMemo} from 'react';
import type {BoxProps} from '@tempo/core';
import {Tr, VStack, Text} from '@tempo/core';
import {useTranslation} from 'next-i18next';
import type {Column} from '@components/Table/types';
import type {TableContextType} from '@components/Table';
import Table, {TableContext} from '@components/Table';
import TableHeader from '@components/Table/core/TableHeader';
import HeaderCell from '@components/Table/cells/HeaderCell';
import {ArtistDiscographyTableColumn} from './types';
import type {Release} from '@deezer/next/services/api/Starmania/requests/album/types';
import ArtistDiscographyTableBody from './ArtistDiscographyTableBody';
import CheckboxCell from '@components/Table/cells/CheckboxCell';

type ArtistDiscographyTableProps = {
	title: string;
	releases: Release[];
	selectedReleaseIds: number[];
	lockedReleaseIdSet: ReadonlySet<number>;
	onSelect: (id: number) => void;
	onSelectAll: (ids: number[]) => void;
} & Omit<BoxProps, 'onSelect'>;

const ArtistDiscographyTableContext =
	TableContext as TableContextType<ArtistDiscographyTableColumn>;

const ArtistDiscographyTable = ({
	title,
	selectedReleaseIds,
	releases,
	lockedReleaseIdSet,
	onSelect,
	onSelectAll,
	...rest
}: ArtistDiscographyTableProps) => {
	const {t} = useTranslation('d4c');

	const columns: Column<ArtistDiscographyTableColumn>[] = useMemo(() => {
		return [
			{
				id: ArtistDiscographyTableColumn.CHECKBOX,
				isCheckbox: true,
				maxWidth: '70px',
			},
			{
				id: ArtistDiscographyTableColumn.TITLE,
				label: t('pitchingtoolform_text_title_d4c-web'),
				minWidth: '230px',
			},
			{
				id: ArtistDiscographyTableColumn.PROVIDER_NAME,
				label: t('deliverymonitoring_title_provider_d4c-web'),
				width: '150px',
			},
			{
				id: ArtistDiscographyTableColumn.LABEL_NAME,
				label: t('pitchingtoolform_text_label_d4c-web'),
				width: '150px',
			},
			{
				id: ArtistDiscographyTableColumn.RELEASE_DATE,
				label: t('pitchingtool_text_releasedatetime_d4c-web'),
				width: '140px',
			},
		];
	}, [t]);

	const selectableIds = useMemo(() => {
		return releases.map(r => r.id).filter(id => !lockedReleaseIdSet.has(id));
	}, [releases, lockedReleaseIdSet]);

	const selectedSelectableCount = useMemo(() => {
		return selectableIds.filter(id => selectedReleaseIds.includes(id)).length;
	}, [selectableIds, selectedReleaseIds]);

	const allSelectableSelected = useMemo(() => {
		if (selectableIds.length === 0) return false;
		return selectedSelectableCount === selectableIds.length;
	}, [selectableIds.length, selectedSelectableCount]);

	const isIndeterminate = useMemo(() => {
		return selectedSelectableCount > 0 && !allSelectableSelected;
	}, [selectedSelectableCount, allSelectableSelected]);

	const isHeaderDisabled = useMemo(() => {
		return selectableIds.length === 0;
	}, [selectableIds.length]);

	const handleSelectAll = useCallback(() => {
		const ids = releases.map(({id}) => id);
		onSelectAll(ids);
	}, [onSelectAll, releases]);

	return (
		<VStack align="left" w="100%">
			<Text variant="body.default.strong">{title}</Text>
			<Table columns={columns} flex={1} variant="contained" {...rest}>
				<ArtistDiscographyTableContext.Consumer>
					{({columns}) => (
						<>
							<TableHeader>
								<Tr>
									{columns.map(column => {
										if (column.isCheckbox) {
											return (
												<CheckboxCell
													data-testid="artist-discography-header-cell-checkbox"
													key={column.id}
													isChecked={allSelectableSelected}
													isIndeterminate={isIndeterminate}
													onChange={handleSelectAll}
													isDisabled={isHeaderDisabled}
													display={column.display}
													isHeader={true}
												/>
											);
										}
										return (
											<HeaderCell
												data-testid={`artist-discography-header-cell-${column.id}`}
												key={column.id}
												label={column.label || ''}
												width={column.width}
												minW={column.minWidth}
												maxW={column.maxWidth}
												display={column.display}
											/>
										);
									})}
								</Tr>
							</TableHeader>

							<ArtistDiscographyTableBody
								data={releases}
								selectedReleaseIds={selectedReleaseIds}
								lockedReleaseIdSet={lockedReleaseIdSet}
								onChange={onSelect}
							/>
						</>
					)}
				</ArtistDiscographyTableContext.Consumer>
			</Table>
		</VStack>
	);
};

export default ArtistDiscographyTable;
